import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Users,
  HelpCircle,
  Settings,
  CheckCircle,
  Clock,
  MessageSquare,
  Star,
  TrendingUp,
  Calendar,
  AlertCircle,
  Loader2,
  UserPlus,
  Plus
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { toast } from "sonner";
import { skillsService } from "@/services/skillsService";
import UpdateSkillsModal from "./UpdateSkillsModal";
import CreateHelpRequestModal from "./CreateHelpRequestModal";
import EditHelpRequestModal from "./EditHelpRequestModal";
import { format } from "date-fns";
import { teamsService } from "@/services/teamsService";
import { wellnessService } from "@/services/wellnessService";
import { useWebSocket } from "@/hooks/useWebSocket";

interface SkillExpert {
  user_id: string;
  skill_name: string;
  expertise_level: string;
  willing_to_mentor: boolean;
  is_available: boolean;
  response_time_avg: string | null;
  success_rate: number | null;
  display_name: string;
  avatar_url: string | null;
}

interface HelpRequest {
  id: string;
  requester_id: string;
  title: string;
  description: string;
  skills_needed: string[];
  priority: string;
  status: string;
  created_at: string;
  requester_name?: string;
  requester_avatar?: string;
}

interface UserAvailability {
  is_available: boolean;
  status_message?: string;
  preferred_connection_method?: string;
}

export default function SkillNetworkEnhanced() {
  const { user } = useUser();
  const { emit, on, off, connected } = useWebSocket();

  // UI State
  const [activeTab, setActiveTab] = useState("directory");
  const [isUpdateSkillsOpen, setIsUpdateSkillsOpen] = useState(false);
  const [isCreateRequestOpen, setIsCreateRequestOpen] = useState(false);
  const [isEditRequestOpen, setIsEditRequestOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<HelpRequest | null>(null);

  // Data State
  const [teamSkills, setTeamSkills] = useState<SkillExpert[]>([]);
  const [helpRequests, setHelpRequests] = useState<HelpRequest[]>([]);
  const [myAvailability, setMyAvailability] = useState<UserAvailability | null>(null);
  const [userSkills, setUserSkills] = useState<string[]>([]);

  // Loading States
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadTeamSkills();
      loadHelpRequests();
      loadMyAvailability();
      loadMySkills();
    }
  }, [user?.id]);

  // WebSocket subscription for real-time help request updates
  useEffect(() => {
    if (!connected || !user?.teamId) return;

    // Join team room for help request updates
    emit('team:join', { teamId: user.teamId });

    const handleNewHelpRequest = () => {
      // Reload help requests to get the full request data
      loadHelpRequests();
    };

    const handleHelpRequestUpdated = (data: { requestId: string; changes: any }) => {
      setHelpRequests((prev) =>
        prev.map((req) =>
          req.id === data.requestId
            ? { ...req, ...data.changes }
            : req
        )
      );
    };

    const handleHelpRequestDeleted = (data: { requestId: string }) => {
      setHelpRequests((prev) => prev.filter((req) => req.id !== data.requestId));
    };

    const cleanup1 = on('skills:help:new', handleNewHelpRequest);
    const cleanup2 = on('skills:help:updated', handleHelpRequestUpdated);
    const cleanup3 = on('skills:help:deleted', handleHelpRequestDeleted);

    return () => {
      emit('team:leave', { teamId: user.teamId });
      cleanup1();
      cleanup2();
      cleanup3();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, user?.teamId, emit, on, off]);

  const loadOrganizationSkills = async () => {
    if (!user?.id || !user?.organizationId) {
      setTeamSkills([]);
      setIsLoadingSkills(false);
      return;
    }

    setIsLoadingSkills(true);
    try {
      // Get organization members and their skills via API
      const { organizationsService } = await import('@/services/organizationsService');
      const membersResponse = await organizationsService.getMembers(user.organizationId);

      if (!membersResponse || !membersResponse.success || !membersResponse.data || membersResponse.data.length === 0) {
        setTeamSkills([]);
        setIsLoadingSkills(false);
        return;
      }

      const orgMemberIds = membersResponse.data.map((m: any) => m.userId || m.id);

      // Get skills for organization members
      const mentorsResponse = await skillsService.getMentors();
      const allSkills = mentorsResponse?.data || [];

      // Filter skills to only organization members
      const orgSkills = allSkills.filter((skill: any) =>
        orgMemberIds.includes(skill.userId || skill.user_id)
      );

      // Transform skills to SkillExpert format
      const transformedSkills: SkillExpert[] = orgSkills.map((skill: any) => ({
        user_id: skill.userId || skill.user_id,
        skill_name: skill.skillName || skill.skill_name,
        expertise_level: skill.proficiencyLevel || skill.expertise_level,
        willing_to_mentor: skill.availability !== false,
        is_available: skill.available !== false,
        response_time_avg: null,
        success_rate: null,
        display_name: skill.user?.displayName || skill.user?.name || 'Unknown',
        avatar_url: skill.user?.avatarUrl || skill.user?.avatar_url || null,
      }));

      setTeamSkills(transformedSkills);
    } catch (error) {
      console.error('Error loading organization skills:', error);
      setTeamSkills([]);
    } finally {
      setIsLoadingSkills(false);
    }
  };

  const loadTeamSkills = async () => {
    if (!user?.id) return;

    // Independent solo users should never see team skills network
    if (user?.persona === "independentSolo") {
      setTeamSkills([]);
      setIsLoadingSkills(false);
      return;
    }

    // Organization solo users should see organization members
    if (user?.persona === "organizationalSolo") {
      await loadOrganizationSkills();
      return;
    }

    setIsLoadingSkills(true);
    try {
      if (!user?.teamId) {
        // User is not in a team - show empty skills
        setTeamSkills([]);
        setIsLoadingSkills(false);
        return;
      }

      // Get team members via API
      const membersResponse = await teamsService.getMembers(user.teamId);

      if (!membersResponse || !membersResponse.success || !membersResponse.data || membersResponse.data.length === 0) {
        setTeamSkills([]);
        setIsLoadingSkills(false);
        return;
      }

      const teamMemberIds = membersResponse.data.map((m: any) => m.id || m.userId);

      // Get skills for team members
      const mentorsResponse = await skillsService.getMentors();
      const allSkills = mentorsResponse?.data || [];

      // Filter skills to only team members
      const teamSkills = allSkills.filter((skill: any) =>
        teamMemberIds.includes(skill.userId || skill.user_id)
      );

      // Transform skills to SkillExpert format
      const transformedSkills: SkillExpert[] = teamSkills.map((skill: any) => ({
        user_id: skill.userId || skill.user_id,
        skill_name: skill.skillName || skill.skill_name,
        expertise_level: skill.proficiencyLevel || skill.expertise_level,
        willing_to_mentor: skill.availability !== false,
        is_available: skill.available !== false,
        response_time_avg: null,
        success_rate: null,
        display_name: skill.user?.displayName || skill.user?.name || 'Unknown',
        avatar_url: skill.user?.avatarUrl || skill.user?.avatar_url || null,
      }));

      setTeamSkills(transformedSkills);
    } catch (error) {
      console.error('Error loading team skills:', error);
      toast.error('Failed to load team skills');
    } finally {
      setIsLoadingSkills(false);
    }
  };

  const loadHelpRequests = async () => {
    if (!user?.id) return;

    setIsLoadingRequests(true);
    try {
      // Get help requests via API
      const response = await skillsService.getHelpRequests({
        status: 'open',
        limit: 50,
      });

      // apiService returns data directly, not wrapped in { data }
      const skillRequests = Array.isArray(response) ? response : (response?.requests || []);

      // Also get check-in based requests via wellness service
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const checkInsResponse = await wellnessService.getCheckIns({
        startDate: sevenDaysAgo.toISOString(),
        hasHelpNeeded: true,
      });

      const checkInRequests = checkInsResponse || [];

      // Transform skill requests
      const formalRequests: HelpRequest[] = skillRequests.map((req: any) => ({
        id: req.id,
        requester_id: req.requesterId || req.requester_id,
        title: req.title || 'Help Request',
        description: req.description || '',
        skills_needed: req.skillsNeeded || req.skills_needed || [],
        priority: req.priority || 'normal',
        status: req.status || 'open',
        created_at: req.createdAt || req.created_at,
        requester_name: req.requester?.displayName || req.requester?.name || 'Unknown',
        requester_avatar: req.requester?.avatarUrl || req.requester?.avatar_url,
      }));

      // Transform check-in requests
      const checkInHelpRequests: HelpRequest[] = checkInRequests
        .filter((checkIn: any) => (checkIn.userId || checkIn.user_id) !== user.id)
        .map((checkIn: any) => {
          const profile = checkIn.user;
          const userId = checkIn.userId || checkIn.user_id;
          return {
            id: `checkin-${checkIn.id}`,
            requester_id: userId,
            title: checkIn.helpWith || checkIn.help_with || 'Help needed',
            description: checkIn.blockers || checkIn.helpWith || checkIn.help_with || 'Help requested in check-in',
            skills_needed: [],
            priority: 'normal',
            status: 'open',
            created_at: checkIn.date || checkIn.createdAt || checkIn.created_at,
            requester_name: profile?.displayName || profile?.name || 'Unknown',
            requester_avatar: profile?.avatarUrl || profile?.avatar_url,
          };
        });

      setHelpRequests([...formalRequests, ...checkInHelpRequests]);
    } catch (error) {
      console.error('Error loading help requests:', error);
      toast.error('Failed to load help requests');
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const loadMyAvailability = async () => {
    if (!user?.id) return;

    try {
      // apiService returns data directly (response.data.data), not wrapped
      const data = await skillsService.getAvailability();

      if (data) {
        // Backend uses isAvailable, preferredConnectionMethod
        setMyAvailability({
          is_available: data.isAvailable !== false,
          status_message: data.statusMessage || '',
          preferred_connection_method: data.preferredConnectionMethod || 'chat',
        });
      } else {
        setMyAvailability({ is_available: true });
      }
    } catch (error) {
      console.warn('Error loading availability, defaulting to available:', error);
      setMyAvailability({ is_available: true });
    }
  };

  const loadMySkills = async () => {
    if (!user?.id) return;

    try {
      // apiService returns data directly (response.data.data), not wrapped
      const skills = await skillsService.getMySkills();

      if (Array.isArray(skills)) {
        setUserSkills(skills
          .filter((skill: any) => skill.availability !== false)
          .map((skill: any) => skill.skillName || skill.skill_name) || []);
      } else {
        setUserSkills([]);
      }
    } catch (error) {
      console.error('Error loading user skills:', error);
      setUserSkills([]);
    }
  };

  const handleConnect = async (expertUserId: string, skillName: string) => {
    if (!user?.id) return;

    try {
      // Create a chat between users via chatService
      const { chatService } = await import('@/services/chatService');
      const chatResponse = await chatService.createIndividualChat({
        recipientId: expertUserId,
      });

      const chatId = chatResponse?.data?.id || chatResponse?.id;

      if (chatId) {
        // Send initial message
        await chatService.sendMessage(chatId, {
          content: `Hi! I'd love to connect about ${skillName}. Would you be available to help?`,
        });

        toast.success('Connection request sent!');
      } else {
        throw new Error('Failed to create chat');
      }
    } catch (error) {
      console.error('Error connecting:', error);
      toast.error('Failed to send connection request');
    }
  };

  const handleOfferHelp = async (requestId: string) => {
    if (!user?.id) return;

    setRespondingTo(requestId);
    try {
      const request = helpRequests.find(r => r.id === requestId);
      if (!request) throw new Error('Request not found');

      // Create a chat between users via chatService
      const { chatService } = await import('@/services/chatService');
      const chatResponse = await chatService.createIndividualChat({
        recipientId: request.requester_id,
      });

      const chatId = chatResponse?.data?.id || chatResponse?.id;

      // Send initial message in the chat
      if (chatId) {
        await chatService.sendMessage(chatId, {
          content: `Hi! I saw your help request "${request.title}" and I can help with that. When would be a good time to connect?`,
        });
      }

      // Handle formal skill requests - respond via skillsService
      if (!requestId.startsWith('checkin-')) {
        try {
          await skillsService.respondToHelpRequest(requestId, {
            message: `I can help with this! I've started a chat with you.`,
          });
        } catch (rpcError) {
          console.warn('Could not respond to help request:', rpcError);
        }
      }

      // Create notification for the requester via notificationsService
      const { notificationsService } = await import('@/services/notificationsService');
      await notificationsService.create({
        type: 'help_offer',
        title: 'Someone offered to help!',
        message: `${user.name || user.displayName || 'A teammate'} offered to help with "${request.title}"`,
        priority: 'normal',
        data: {
          helperId: user.id,
          helperName: user.name || user.displayName,
          requestId: requestId,
          chatId: chatId,
          actionUrl: chatId ? `/chat/${chatId}` : null,
        },
      });

      toast.success('Help offer sent! A chat has been created for you to connect.');

      // Remove the request from view after offering help
      setHelpRequests(helpRequests.filter(r => r.id !== requestId));
    } catch (error) {
      console.error('Error offering help:', error);
      toast.error('Failed to send help offer');
    } finally {
      setRespondingTo(null);
    }
  };

  const handleDecline = async (requestId: string) => {
    // Simply remove from view - could also track declines in database
    setHelpRequests(helpRequests.filter(r => r.id !== requestId));
  };

  const handleEditRequest = async (requestId: string) => {
    const request = helpRequests.find(r => r.id === requestId);
    if (request && !requestId.startsWith('checkin-')) {
      setEditingRequest(request);
      setIsEditRequestOpen(true);
    } else if (requestId.startsWith('checkin-')) {
      toast.error('Cannot edit check-in based help requests');
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!user?.id) return;

    try {
      if (requestId.startsWith('checkin-')) {
        // Can't delete check-in based requests
        toast.error('Cannot delete check-in based help requests');
        return;
      }

      // Update help request status via skillsService
      const response = await skillsService.updateHelpRequest(requestId, {
        status: 'cancelled',
      });

      if (!response || !response.success) {
        throw new Error(response?.error || 'Failed to cancel help request');
      }

      // Create notifications for users who might have seen this request
      const request = helpRequests.find(r => r.id === requestId);
      if (request && request.skills_needed.length > 0) {
        try {
          // Find users who have matching skills via skillsService
          const mentorsResponse = await skillsService.getMentors({
            skillId: request.skills_needed[0], // Use first skill
            availability: true,
          });

          const potentialViewers = mentorsResponse?.data || [];

          if (potentialViewers.length > 0) {
            // Create notifications via notificationsService
            const { notificationsService } = await import('@/services/notificationsService');
            for (const viewer of potentialViewers) {
              if ((viewer.userId || viewer.user_id) !== user.id) {
                await notificationsService.create({
                  type: 'request_cancelled',
                  title: 'Help request cancelled',
                  message: `"${request.title}" is no longer needed`,
                  priority: 'low',
                  data: {
                    requestId: requestId,
                    requesterId: user.id,
                    cancelledAt: new Date().toISOString()
                  },
                });
              }
            }
          }
        } catch (notifError) {
          console.warn('Could not send cancellation notifications:', notifError);
        }
      }

      // Remove from local state immediately
      setHelpRequests(helpRequests.filter(r => r.id !== requestId));
      toast.success('Help request cancelled and team notified');
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete help request');
    }
  };

  const getExpertiseIcon = (level: string) => {
    switch (level) {
      case 'expert': return <Star className="h-4 w-4 text-yellow-500" />;
      case 'advanced': return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'intermediate': return <CheckCircle className="h-4 w-4 text-green-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'default';
      case 'normal': return 'secondary';
      default: return 'outline';
    }
  };

  // Filter help requests based on user's skills and availability
  const relevantHelpRequests = helpRequests.filter(request => {
    // Don't show user's own requests
    if (request.requester_id === user?.id) return false;

    // Only show if user is available
    if (!myAvailability?.is_available) return false;

    // If no skills specified, check if user has any skills at all
    if (request.skills_needed.length === 0) {
      return userSkills.length > 0;
    }

    // Show only if user has matching skills
    return request.skills_needed.some(requestedSkill =>
      userSkills.some(userSkill =>
        userSkill.toLowerCase() === requestedSkill.toLowerCase() ||
        userSkill.toLowerCase().includes(requestedSkill.toLowerCase()) ||
        requestedSkill.toLowerCase().includes(userSkill.toLowerCase())
      )
    );
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Skill Network</CardTitle>
          <CardDescription>
            Connect with teammates, share expertise, and get help when needed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="directory">Team Skills</TabsTrigger>
              <TabsTrigger value="requests" className="relative">
                Help Requests
                {relevantHelpRequests.length > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 rounded-full">
                    {relevantHelpRequests.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="my-requests">My Requests</TabsTrigger>
              <TabsTrigger value="availability">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="directory" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Team Skill Directory</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreateRequestOpen(true)}
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Request Help
                </Button>
              </div>

              {isLoadingSkills ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : teamSkills.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  No team skills found. Be the first to add your skills!
                </p>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {/* Group skills by skill name */}
                    {Object.entries(
                      teamSkills.reduce((acc, expert) => {
                        if (!acc[expert.skill_name]) {
                          acc[expert.skill_name] = [];
                        }
                        acc[expert.skill_name].push(expert);
                        return acc;
                      }, {} as Record<string, SkillExpert[]>)
                    ).map(([skillName, experts]) => (
                      <div key={skillName} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium">{skillName}</h4>
                          <Badge variant="outline">
                            {experts.length} {experts.length === 1 ? 'expert' : 'experts'}
                          </Badge>
                        </div>

                        <div className="space-y-2">
                          {experts.map((expert) => (
                            <div
                              key={`${expert.user_id}-${expert.skill_name}`}
                              className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                            >
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={expert.avatar_url || undefined} />
                                  <AvatarFallback>
                                    {expert.display_name?.[0]?.toUpperCase() || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm">{expert.display_name}</span>
                                    {expert.user_id === user?.id && (
                                      <Badge variant="secondary" className="text-xs">You</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {getExpertiseIcon(expert.expertise_level)}
                                    <span className="text-xs text-muted-foreground capitalize">
                                      {expert.expertise_level}
                                    </span>
                                    {expert.willing_to_mentor ? (
                                      <Badge variant="default" className="text-xs">Mentor</Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-xs">Learning</Badge>
                                    )}
                                    {!expert.is_available && (
                                      <Badge variant="outline" className="text-xs">
                                        Busy
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {expert.user_id !== user?.id && expert.willing_to_mentor && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleConnect(expert.user_id, expert.skill_name)}
                                  disabled={!expert.is_available}
                                >
                                  <UserPlus className="h-3 w-3 mr-1" />
                                  Connect
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="requests" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Help Requests</h3>
                <div className="text-sm text-muted-foreground">
                  {myAvailability?.is_available
                    ? `Showing ${relevantHelpRequests.length} requests matching your skills`
                    : "Set yourself as available to see requests"
                  }
                </div>
              </div>

              {isLoadingRequests ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : relevantHelpRequests.length === 0 ? (
                <div className="text-center py-8">
                  <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    No help requests matching your skills at the moment
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {relevantHelpRequests.map((request) => (
                      <div key={request.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={request.requester_avatar || undefined} />
                              <AvatarFallback>
                                {request.requester_name?.[0]?.toUpperCase() || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h4 className="font-medium">{request.title}</h4>
                              <p className="text-sm text-muted-foreground">
                                by {request.requester_name}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={getPriorityColor(request.priority)}>
                              {request.priority}
                            </Badge>
                            <Badge variant="outline">
                              <Calendar className="h-3 w-3 mr-1" />
                              {format(new Date(request.created_at), 'MMM d')}
                            </Badge>
                          </div>
                        </div>

                        <p className="text-sm mb-3">{request.description}</p>

                        {request.skills_needed.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {request.skills_needed.map((skill) => (
                              <Badge key={skill} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        )}

                        <div className="flex justify-end gap-2">
                          {request.requester_id === user?.id ? (
                            // User's own request - show edit/delete options
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditRequest(request.id)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteRequest(request.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                Delete
                              </Button>
                            </>
                          ) : (
                            // Other's request - show offer help/decline
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDecline(request.id)}
                              >
                                Decline
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleOfferHelp(request.id)}
                                disabled={respondingTo === request.id}
                              >
                                {respondingTo === request.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <MessageSquare className="h-4 w-4 mr-1" />
                                    Offer Help
                                  </>
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="my-requests" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">My Help Requests</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreateRequestOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Request
                </Button>
              </div>

              {isLoadingRequests ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {helpRequests
                      .filter(request => request.requester_id === user?.id)
                      .map((request) => (
                        <div key={request.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium">{request.title}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant={getPriorityColor(request.priority)}>
                                  {request.priority}
                                </Badge>
                                <Badge variant="outline">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  {format(new Date(request.created_at), 'MMM d')}
                                </Badge>
                                <Badge variant={request.status === 'open' ? 'default' : 'secondary'}>
                                  {request.status}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditRequest(request.id)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteRequest(request.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                Delete
                              </Button>
                            </div>
                          </div>

                          <p className="text-sm mb-3">{request.description}</p>

                          {request.skills_needed.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {request.skills_needed.map((skill) => (
                                <Badge key={skill} variant="secondary" className="text-xs">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                    {helpRequests.filter(request => request.requester_id === user?.id).length === 0 && (
                      <div className="text-center py-8">
                        <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">
                          No help requests yet. Create your first request!
                        </p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="availability" className="space-y-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-medium mb-3">My Skills and Availability</h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Open to help requests</span>
                    <Badge
                      variant={myAvailability?.is_available ? "default" : "outline"}
                      className={myAvailability?.is_available ? "bg-green-100 text-green-700" : ""}
                    >
                      {myAvailability?.is_available ? "Available" : "Unavailable"}
                    </Badge>
                  </div>

                  {myAvailability?.status_message && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-sm flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        {myAvailability.status_message}
                      </p>
                    </div>
                  )}

                  {userSkills.length > 0 ? (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">
                        You're currently visible to the team for help with:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {userSkills.map((skill) => (
                          <Badge key={skill} variant="secondary">
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Add skills to be visible for help requests
                    </p>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setIsUpdateSkillsOpen(true)}
                  >
                    <Settings size={14} className="mr-1" />
                    Update Skills & Availability
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <UpdateSkillsModal
        isOpen={isUpdateSkillsOpen}
        onClose={() => {
          setIsUpdateSkillsOpen(false);
          loadTeamSkills();
          loadMyAvailability();
          loadMySkills();
        }}
      />

      <CreateHelpRequestModal
        isOpen={isCreateRequestOpen}
        onClose={() => {
          setIsCreateRequestOpen(false);
          loadHelpRequests();
        }}
      />

      <EditHelpRequestModal
        isOpen={isEditRequestOpen}
        onClose={() => {
          setIsEditRequestOpen(false);
          setEditingRequest(null);
        }}
        request={editingRequest}
        onUpdate={() => {
          loadHelpRequests();
        }}
      />
    </div>
  );
}